import { ApplicationController } from "../../support/application-controller";

export default class extends ApplicationController {
    static targets = [
        "cardToggle",
        "tableToggle",
        "cardPane",
        "tablePane",
        "dataSource",
        "tableBody",
        "filterPanel",
        "filterToggle",
        "quickFiltersPanel",
        "resultsCount"
    ];

    // Filter parameter names to check for URL params and active filter counting
    static filterParams = [
        "owner_info",
        "subscription_status",
        "subscription_product_id",
        "subscription_invoice_status",
        "subscription_invoice_number",
        "subscription_type",
        "subscription_customer_type",
        "subscription_maximum_concurrent_users",
        "subscription_expiration_range",
        "payment_expiration_range",
        "price",
        "external_search_source_type",
        "external_search_source_id"
    ];

    connect() {
        this.storageKey = this.data.get("storageKey") || "adminSubscriptionsView";
        this.defaultView = this.data.get("defaultView") || "cards";
        this.filterPanelStorageKey = this.data.get("filterPanelStorageKey") || "adminSubscriptionsFilterPanel";
        this.filterPanelDefaultState = this.data.get("filterPanelDefaultState") || "collapsed";

        this.subscriptionsPayload = this._parsePayload();
        this._subscriptionsAppendedHandler = this._handleSubscriptionsAppended.bind(this);
        this.element.addEventListener("subscriptions:appended", this._subscriptionsAppendedHandler);
        this.showView(this._preferredView());

        this._restoreFilterPanelState();
    }

    disconnect() {
        this.element.removeEventListener("subscriptions:appended", this._subscriptionsAppendedHandler);
    }

    showCards(event) {
        if (event) event.preventDefault();
        this.showView("cards");
    }

    showTable(event) {
        if (event) event.preventDefault();
        this.showView("table");
    }

    showView(view) {
        const normalizedView = view === "table" ? "table" : "cards";
        if (this.hasCardPaneTarget) {
            this._toggleHidden(this.cardPaneTarget, normalizedView !== "cards");
        }
        if (this.hasTablePaneTarget) {
            this._toggleHidden(this.tablePaneTarget, normalizedView !== "table");
        }

        // Quick filters are only available in table view
        if (this.hasQuickFiltersPanelTarget) {
            this._toggleHidden(this.quickFiltersPanelTarget, normalizedView !== "table");
        }

        if (this.hasCardToggleTarget) {
            this._setToggleState(this.cardToggleTarget, normalizedView === "cards");
        }
        if (this.hasTableToggleTarget) {
            this._setToggleState(this.tableToggleTarget, normalizedView === "table");
        }

        this._persistView(normalizedView);
    }

    // Filter panel toggle methods
    toggleFilterPanel(event) {
        if (event) event.preventDefault();
        const isCurrentlyCollapsed = this._isFilterPanelCollapsed();
        const newState = isCurrentlyCollapsed ? "expanded" : "collapsed";
        this._setFilterPanelState(newState);
        this._persistFilterPanelState(newState);
    }

    expandFilterPanel(event) {
        if (event) event.preventDefault();
        this._setFilterPanelState("expanded");
        this._persistFilterPanelState("expanded");
    }

    collapseFilterPanel(event) {
        if (event) event.preventDefault();
        this._setFilterPanelState("collapsed");
        this._persistFilterPanelState("collapsed");
    }

    _isFilterPanelCollapsed() {
        if (!this.hasFilterPanelTarget) return true;
        return this.filterPanelTarget.classList.contains("subscriptions-filter-panel--collapsed");
    }

    _setFilterPanelState(state) {
        if (!this.hasFilterPanelTarget) return;

        const isCollapsed = state === "collapsed";

        this.filterPanelTarget.classList.toggle("subscriptions-filter-panel--collapsed", isCollapsed);
        this.filterPanelTarget.classList.toggle("subscriptions-filter-panel--expanded", !isCollapsed);

        if (this.hasFilterToggleTarget) {
            this.filterToggleTarget.setAttribute("aria-expanded", isCollapsed ? "false" : "true");
        }
    }

    _restoreFilterPanelState() {
        // Only use the user's persisted preference - don't auto-expand based on URL params
        // This ensures quick filters don't unexpectedly expand the detailed filter panel
        const preferredState = this._preferredFilterPanelState();
        this._setFilterPanelState(preferredState);
    }

    _persistFilterPanelState(state) {
        try {
            window.localStorage.setItem(this.filterPanelStorageKey, state);
        } catch (error) {
            // no-op if storage unavailable
        }
    }

    _preferredFilterPanelState() {
        try {
            return window.localStorage.getItem(this.filterPanelStorageKey) || this.filterPanelDefaultState;
        } catch (error) {
            return this.filterPanelDefaultState;
        }
    }

    _hasFilterParams() {
        try {
            const urlParams = new URLSearchParams(window.location.search);
            return this.constructor.filterParams.some(param => {
                const value = urlParams.get(param);
                return value !== null && value !== "";
            });
        } catch (error) {
            return false;
        }
    }

    _toggleHidden(element, hidden) {
        if (!element) return;
        if (hidden) {
            element.classList.add("hidden");
        } else {
            element.classList.remove("hidden");
        }
    }

    _setToggleState(button, isActive) {
        if (!button) return;
        button.classList.toggle("subscriptions-view-toggle__button--active", isActive);
        button.setAttribute("aria-pressed", isActive ? "true" : "false");
    }

    _persistView(view) {
        try {
            window.localStorage.setItem(this.storageKey, view);
        } catch (error) {
            // no-op if storage unavailable
        }
    }

    _preferredView() {
        try {
            return window.localStorage.getItem(this.storageKey) || this.defaultView;
        } catch (error) {
            return this.defaultView;
        }
    }

    _parsePayload() {
        if (!this.hasDataSourceTarget) return [];
        try {
            return JSON.parse(this.dataSourceTarget.textContent || "[]");
        } catch (error) {
            console.warn("Unable to parse subscriptions payload", error);
            return [];
        }
    }

    _handleSubscriptionsAppended(event) {
        const detail = event.detail || {};
        if (detail.subscriptions && detail.subscriptions.length && this.hasDataSourceTarget) {
            this.subscriptionsPayload = (this.subscriptionsPayload || []).concat(detail.subscriptions);
            this.dataSourceTarget.textContent = JSON.stringify(this.subscriptionsPayload);
        }

        if (detail.tableRows && detail.tableRows.length && this.hasTableBodyTarget) {
            this.tableBodyTarget.insertAdjacentHTML("beforeend", detail.tableRows);
        }

        // Update the results count display
        this._updateResultsCount(detail.canLoadMore);
    }

    _updateResultsCount(canLoadMore) {
        if (!this.hasResultsCountTarget) return;

        // Count the current number of subscriptions from the payload
        const count = this.subscriptionsPayload ? this.subscriptionsPayload.length : 0;
        const suffix = canLoadMore ? "+" : "";
        this.resultsCountTarget.textContent = `${count}${suffix} results`;
        this.resultsCountTarget.dataset.canLoadMore = canLoadMore;
    }
}
